import type { AuditEvent } from "../../api/types";
import type { PillTone } from "../../components/StatePill";
import { formatAuditLine } from "../admin/audit/auditFormat";

/**
 * Home's "Recent activity" list (IA slice 1) reads the SAME audit-events
 * endpoint Audit log/Governance Decisions already use (A-5 — one read path,
 * no second fetch mechanism) rather than a bespoke "activity feed" API that
 * doesn't exist. It is real data only (A-10): no row here is fabricated,
 * and a tenant with nothing in its trail yet gets no section at all (see
 * `RecentActivity.tsx`) — the Home hero IS that empty state.
 *
 * `listAuditEvents` is `audit_log:view`-scoped (admin-only,
 * server/internal/authz/model.go) — there is no cheaper fleet-wide read an
 * operator/viewer role can call, so this whole facet is honestly omitted
 * for those roles rather than faked or silently 403ing (see HomePage.tsx).
 */

export interface ActivityPill {
  label: string;
  tone: PillTone;
}

/**
 * Maps a real audit event to the mock's pill vocabulary (deployed/gated/
 * alert) where the event genuinely means that, and falls back to the
 * server's own `outcome` string in a neutral pill otherwise — never invents
 * a category the event doesn't support (A-10).
 */
export function activityPill(event: AuditEvent): ActivityPill {
  if (event.action === "service.deploy" && event.outcome === "success") {
    return { label: "deployed", tone: "positive" };
  }
  if (event.action === "governance.decision") {
    return event.outcome === "deny" ? { label: "gated", tone: "warning" } : { label: "allowed", tone: "positive" };
  }
  if (event.outcome === "failure" || event.outcome === "deny") {
    return { label: "alert", tone: "negative" };
  }
  return { label: event.outcome, tone: "neutral" };
}

/** Plain-language description for a Home activity row — delegates to the
 * ONE shared audit-line formatter (`formatAuditLine`, UX polish wave 3, A-5)
 * so Home and the Audit log's Actor column stay in sync by construction
 * rather than each hand-copying "actor — action · resource" formatting. See
 * that function's doc comment for what it deliberately omits (the raw
 * `event.target` id — a full verb lexicon that names the target in plain
 * language is a later founder call, see the module doc comment above). */
export function activityDescription(event: AuditEvent): string {
  return formatAuditLine(event);
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Relative "Xm/Xh/Xd ago" formatting, matching
 * `features/services/DeploymentTimeline.tsx`'s own local helper — kept
 * separate rather than extracted into a shared util for one more use site
 * (not yet a third repetition, A-12's threshold). */
export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const abs = Math.abs(diffMs);
  if (abs < MINUTE_MS) return "just now";
  if (abs < HOUR_MS) return `${Math.round(diffMs / MINUTE_MS)}m ago`;
  if (abs < DAY_MS) return `${Math.round(diffMs / HOUR_MS)}h ago`;
  return `${Math.round(diffMs / DAY_MS)}d ago`;
}
